import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

type GenerateWorkoutPdfOptions = {
  element: HTMLElement;
  workoutName: string;
  footerElement?: HTMLElement | null;
};

export const generateWorkoutPdf = async ({
  element,
  workoutName,
  footerElement,
}: GenerateWorkoutPdfOptions): Promise<void> => {
  try {
    // Capture the main content as a canvas
    const canvas = await html2canvas(element, {
      backgroundColor: '#FFFFFF',
      scale: 2,
    });

    // Capture footer element if provided
    let footerCanvas: HTMLCanvasElement | null = null;
    if (footerElement) {
      footerCanvas = await html2canvas(footerElement, {
        backgroundColor: '#FFFFFF',
        scale: 2,
      });
    }

    // Create PDF (defaults to A4)
    const pdf = new jsPDF({
      orientation: 'portrait',
    });

    // Get A4 dimensions from PDF instance
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    // Set white background for all pages
    const setWhiteBackground = () => {
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, 0, pdfWidth, pdfHeight, 'F');
    };

    // Define padding (in mm)
    const padding = 18;
    const footerPadding = 0;
    const contentWidth = pdfWidth - padding * 2;
    const contentHeight = pdfHeight - padding * 2;

    // Helper to mask the header and footer areas to prevent image bleeding
    const maskMargins = () => {
      pdf.setFillColor(255, 255, 255);
      // Mask top margin
      pdf.rect(0, 0, pdfWidth, padding, 'F');
      // Mask bottom margin
      pdf.rect(0, pdfHeight - padding, pdfWidth, padding, 'F');
    };

    // Helper to add footer to current page
    const addFooter = () => {
      if (!footerCanvas) {
        return;
      }

      const footerImgData = footerCanvas.toDataURL('image/png');
      const footerAspectRatio = footerCanvas.width / footerCanvas.height;
      const footerHeight = 5; // Height in mm
      const footerWidth = footerHeight * footerAspectRatio;
      const footerX = pdfWidth - padding - footerWidth - footerPadding; // Align to right with padding
      const footerY = pdfHeight - padding + (padding - footerHeight) / 2 - footerPadding / 2;
      pdf.addImage(footerImgData, 'PNG', footerX, footerY, footerWidth, footerHeight);
    };

    const imgWidth = canvas.width;
    const imgHeight = canvas.height;

    // Calculate aspect ratio and scale to fit A4 width with padding
    const imgAspectRatio = imgWidth / imgHeight;
    const scaledWidth = contentWidth;
    const scaledHeight = contentWidth / imgAspectRatio;

    // Convert canvas to image data
    const imgData = canvas.toDataURL('image/jpeg');

    // Calculate how many pages we need
    let heightLeft = scaledHeight;
    let position = padding;

    // Set white background and add first page
    setWhiteBackground();
    pdf.addImage(imgData, 'jpeg', padding, position, scaledWidth, scaledHeight);
    maskMargins();
    addFooter();
    heightLeft -= contentHeight;

    // Add additional pages if content exceeds one page
    while (heightLeft > 0) {
      // Calculate position to show the next portion of the image
      // Negative position shifts the image up to show the next section
      position = padding - (scaledHeight - heightLeft);
      pdf.addPage();
      setWhiteBackground();
      pdf.addImage(imgData, 'jpeg', padding, position, scaledWidth, scaledHeight);
      maskMargins();
      addFooter();
      heightLeft -= contentHeight;
    }

    // Sanitize workout name for filename
    const sanitizedWorkoutName = workoutName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const filename = `${sanitizedWorkoutName}_workout.pdf`;

    // Save the PDF
    pdf.save(filename);
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};
